<template>
    <AccordionBasic
        title="contract_class_details"
        class="mb-2"
        :open="true"
    >
        <template
            v-for="field in fields"
            :key="field.id"
        >
            <NumberField
                v-if="
                    field.type === 'number' &&
                    !!assetClass &&
                    field.appliedTo.includes(assetClass)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <TextField
                v-if="
                    field.type === 'text' &&
                    !!assetClass &&
                    field.appliedTo.includes(assetClass)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <ToggleSwitch
                v-if="
                    field.type === 'toggle' &&
                    !!assetClass &&
                    field.appliedTo.includes(assetClass)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <SelectField
                v-if="
                    field.type === 'select' &&
                    !!assetClass &&
                    field.appliedTo.includes(assetClass)
                "
                :name="field.id"
                :label="field.label"
                :required="field.required ?? false"
                :options="field.options ?? []"
                :model-value="getFieldValue(field)"
                @update:model-value="setFieldValue(field.id, $event)"
            />
            <RepaymentScheduleEditor
                v-if="
                    field.type === 'schedule' &&
                    !!assetClass &&
                    field.appliedTo.includes(assetClass) &&
                    contract.repayment_type === RepaymentType.IRREGULAR
                "
                :model-value="contract.repayment_schedule"
                @update:model-value="
                    setFieldValue('repayment_schedule', $event)
                "
            />
        </template>
    </AccordionBasic>
</template>

<script setup lang="ts">
import AccordionBasic from '@src/components/AccordionBasic.vue'
import NumberField from '@src/components/NumberField.vue'
import SelectField from '@src/components/SelectField.vue'
import TextField from '@src/components/TextField.vue'
import ToggleSwitch from '@src/components/ToggleSwitch.vue'
import { useEnum } from '@src/composables/common'
import {
    AssetClass,
    ConversionPriceFormula,
    ConversionTriggerType,
    RepaymentType,
} from '@src/enums'
import { ContractDetails } from '@src/types'
import { watch } from 'vue'
import RepaymentScheduleEditor from './RepaymentScheduleEditor.vue'

const props = defineProps<{
    contract: ContractDetails
    assetClass: AssetClass | null
}>()

const { mapEnumToArray } = useEnum()

function getFieldValue(field: { id: string; type: string }): any {
    const value = props.contract[field.id as keyof ContractDetails]
    if (value !== undefined && value !== null) {
        return value
    }
    return field.type === 'toggle' ? false : null
}

function setFieldValue(id: string, value: unknown) {
    ;(props.contract as any)[id] = value
}

// Field groups per the (corrected) asset-class catalog - kept as separate,
// explicit appliedTo lists per field rather than one shared condition, since
// the applicable class sets genuinely differ between groups.
const convertibleBondOnly = [AssetClass.CONVERTIBLE_BOND]
const bondAndConvertibleBond = [AssetClass.BOND, AssetClass.CONVERTIBLE_BOND]
const profitParticipationGroup = [
    AssetClass.PROFIT_PARTICIPATION,
    AssetClass.PROFIT_PARTICIPATION_SPV,
]
const debtAndMezzanineGroup = [
    AssetClass.BOND,
    AssetClass.CONVERTIBLE_BOND,
    AssetClass.SUBORDINATED_LOAN,
    AssetClass.PROFIT_PARTICIPATION,
    AssetClass.PROFIT_PARTICIPATION_SPV,
]

const fields = [
    // Convertible bond only
    {
        id: 'conversion_trigger_type',
        label: 'conversion_trigger_type',
        type: 'select',
        options: mapEnumToArray(ConversionTriggerType),
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_price_formula',
        label: 'conversion_price_formula',
        type: 'select',
        options: mapEnumToArray(ConversionPriceFormula),
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_price_reference_days',
        label: 'conversion_price_reference_days',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_price_floor',
        label: 'conversion_price_floor',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_price_discount_pct',
        label: 'conversion_price_discount_pct',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_price_final',
        label: 'conversion_price_final',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_date_annual',
        label: 'conversion_date_annual',
        type: 'text',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_window_open_days',
        label: 'conversion_window_open_days',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_conditions',
        label: 'conversion_conditions',
        type: 'text',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'conversion_valuation',
        label: 'conversion_valuation',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'disagio_pct',
        label: 'disagio_pct',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'early_repayment_price_threshold_pct',
        label: 'early_repayment_price_threshold_pct',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: convertibleBondOnly,
    },
    {
        id: 'early_repayment_observation_days',
        label: 'early_repayment_observation_days',
        type: 'number',
        appliedTo: convertibleBondOnly,
    },

    // Bond + convertible_bond only
    {
        id: 'collateral_description',
        label: 'collateral_description',
        type: 'text',
        appliedTo: bondAndConvertibleBond,
    },

    // Profit participation + PPC-SPV
    {
        id: 'gs_participation_rate_max',
        label: 'gs_participation_rate_max',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'share_capital_base',
        label: 'share_capital_base',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'min_interest_rate_pa',
        label: 'min_interest_rate_pa',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'ebit_multiple',
        label: 'ebit_multiple',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'revenue_multiple',
        label: 'revenue_multiple',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'settlement_costs_pct',
        label: 'settlement_costs_pct',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'participation_share_per_gs',
        label: 'participation_share_per_gs',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'cancellation_possible_from',
        label: 'cancellation_possible_from',
        type: 'text',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'termination_notice_months',
        label: 'termination_notice_months',
        type: 'number',
        appliedTo: profitParticipationGroup,
    },
    {
        id: 'termination_reference_date',
        label: 'termination_reference_date',
        type: 'text',
        appliedTo: profitParticipationGroup,
    },

    // Debt + mezzanine generally
    {
        id: 'repayment_type',
        label: 'repayment_type',
        type: 'select',
        options: mapEnumToArray(RepaymentType),
        appliedTo: debtAndMezzanineGroup,
    },
    {
        id: 'repayment_schedule',
        label: 'repayment_schedule',
        type: 'schedule',
        appliedTo: debtAndMezzanineGroup,
    },
    {
        id: 'accrued_interest_applicable',
        label: 'accrued_interest_applicable',
        type: 'toggle',
        appliedTo: debtAndMezzanineGroup,
    },
    {
        id: 'default_interest_rate',
        label: 'default_interest_rate',
        type: 'number',
        appliedTo: debtAndMezzanineGroup,
    },
    {
        id: 'early_repayment_min_yield_pct',
        label: 'early_repayment_min_yield_pct',
        type: 'number',
        validation: { min: 0, max: 100 },
        appliedTo: debtAndMezzanineGroup,
    },
    {
        id: 'in_kind_interest_rate',
        label: 'in_kind_interest_rate',
        type: 'number',
        appliedTo: debtAndMezzanineGroup,
    },
]

// Every field above is asset_class-specific (nullable server-side) - unlike
// AssetClassesAccordion.vue there's no "applies to every class" field to protect
// here, so a plain reset on every class change (including asset_uid switching to a
// different asset) is safe.
watch(
    () => props.assetClass,
    () => {
        const currentAssetClass = props.assetClass
        const currentContract = props.contract
        fields.forEach((field) => {
            const applies =
                !!currentAssetClass &&
                field.appliedTo.includes(currentAssetClass)
            if (!applies) {
                const value = currentContract[field.id as keyof ContractDetails]
                if (value !== null) {
                    ;(currentContract as any)[field.id] = null
                }
            }
        })
    },
    { immediate: true }
)
</script>
